/**
 * Breadcrumb trail (Phase 14) — observability only.
 * ------------------------------------------------
 * A tiny in-memory ring buffer of the last N user/system actions.
 * Attached to error events so a crash can be reconstructed without
 * ever transmitting customer data.
 *
 * Rules:
 *   • never throws
 *   • never stores PII (ids only, no names/emails/registrations)
 *   • no timers, no network, no persistence of its own
 */

export type BreadcrumbCategory =
  | "auth"
  | "navigation"
  | "job"
  | "appraisal"
  | "photo"
  | "signature"
  | "invoice"
  | "sync"
  | "network"
  | "push"
  | "native"
  | "system";

export interface Breadcrumb {
  ts: string;
  category: BreadcrumbCategory;
  message: string;
  data?: Record<string, string | number | boolean>;
}

const MAX_BREADCRUMBS = 25;
const trail: Breadcrumb[] = [];

/** Record a breadcrumb. Safe to call from anywhere; never throws. */
export function addBreadcrumb(
  category: BreadcrumbCategory,
  message: string,
  data?: Record<string, string | number | boolean>,
): void {
  try {
    trail.push({
      ts: new Date().toISOString(),
      category,
      message: String(message).slice(0, 120),
      data: data ? sanitise(data) : undefined,
    });
    if (trail.length > MAX_BREADCRUMBS) trail.splice(0, trail.length - MAX_BREADCRUMBS);
  } catch {
    /* breadcrumbs must never break the app */
  }
}

/** Only primitive, non-identifying values survive. */
function sanitise(
  data: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(data).slice(0, 8)) {
    if (typeof v === "number" || typeof v === "boolean") out[k] = v;
    else if (typeof v === "string") out[k] = v.slice(0, 60);
  }
  return out;
}

/** Snapshot of the current trail (oldest first). */
export function getBreadcrumbs(): Breadcrumb[] {
  return trail.slice();
}

/** Clear the trail (used on sign-out so trails never cross identities). */
export function clearBreadcrumbs(): void {
  trail.length = 0;
}
